import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {type RefObject, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";

import {buildStreetWithHouseNumber} from "@reva/shared/geo-reference";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import {useRevaTranslation, useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {clearPurposeChoice, selectPurposeChoiceData} from "../../redux/addressSlice";
import {fetchPersistence, initializePersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";

/** Hotkeys, falls die Übersetzung keinen eigenen vorgibt (`purpose.choiceDialog.hotkey.<ZWECK>`). */
const DEFAULT_HOTKEYS: Record<RevaValuationPurpose, string> = {RESEARCH: "R", VALUATION: "B"};

interface PurposeChoiceDialogProps {
    /**
     * Wofür diese Instanz zuständig ist: `address` = Neuanlage über die Startseite, `order` = Erstimport eines Auftrags
     * aus EVA+ (`?order=&client=`, an der App eingehängt, weil der Sprung auf jeder Seite landen kann).
     */
    pending: "address" | "order";
    /** Fokusziel nach dem Abbrechen (nur `address`). */
    addressInputRef?: RefObject<HTMLTextAreaElement | null>;
}

const PURPOSE_ICONS: Record<RevaValuationPurpose, string> = {
    RESEARCH: PrimeIcons.SEARCH,
    VALUATION: "pi pi-file-edit",
};

/**
 * #19481: Modaler Dialog vor dem Anlegen einer neuen REVA-ID, wenn der User mehrere Zwecke anlegen darf: Research
 * (nur mal zu einer Adresse schauen) oder Bewertung (bis zur Finalisierung). Die Wahl legt die REVA-ID direkt an; der
 * Doppler-Check ist dann schon gelaufen bzw. beim EVA+-Sprung steht fest, dass der Auftrag noch keine REVA-ID hat.
 * Abbrechen legt nichts an.
 */
export default function PurposeChoiceDialog({pending, addressInputRef}: PurposeChoiceDialogProps) {
    const {t, language} = useRevaTranslation();
    const selectByCurrentLanguage = useSelectByCurrentLanguage();
    const dispatch = useRevaDispatch();
    const navigate = useNavigate();
    const allChoiceData = useRevaSelector(selectPurposeChoiceData);
    const choiceData = (pending === "order") === !!allChoiceData?.pendingOrder ? allChoiceData : null;
    const forOrder = pending === "order";

    const onChoose = (purpose: RevaValuationPurpose) => {
        const pendingAddress = choiceData?.pendingAddress;
        const pendingOrder = choiceData?.pendingOrder;
        dispatch(clearPurposeChoice());
        if (pendingAddress) {
            void dispatch(initializePersistence(pendingAddress, {skipDuplicateCheck: true, purpose}));
        } else if (pendingOrder) {
            void dispatch(fetchPersistence({...pendingOrder, purpose}));
        }
    };

    const onCancel = () => {
        dispatch(clearPurposeChoice());
        if (forOrder) {
            // Ohne Wahl bleibt der Auftrag unimportiert; ?order=&client= fällt weg, damit nichts erneut fragt.
            void navigate("/", {replace: true});
        } else {
            addressInputRef?.current?.focus?.();
        }
    };

    const options = choiceData?.options ?? [];
    const optionsKey = options.join();
    const listRef = useRef<HTMLDivElement>(null);
    const hotkeyOf = (purpose: RevaValuationPurpose) =>
        t(`purpose.choiceDialog.hotkey.${purpose}`, {defaultValue: DEFAULT_HOTKEYS[purpose]}).toUpperCase();

    // Tastatur: Pfeil hoch/runter wandert (zyklisch) durch die Zwecke, der Hotkey (R/B) wählt direkt. Auf
    // Dokumentebene, damit es auch greift, wenn der Fokus nach einem Klick in den Dialog nicht auf einem Button liegt.
    useEffect(() => {
        if (!optionsKey) return undefined;
        const purposes = optionsKey.split(",") as RevaValuationPurpose[];
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            const buttons = Array.from(listRef.current?.querySelectorAll("button") ?? []);
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                const current = buttons.findIndex((button) => button === document.activeElement);
                const step = event.key === "ArrowDown" ? 1 : -1;
                const next = current < 0 ? 0 : (current + step + buttons.length) % buttons.length;
                buttons[next]?.focus();
                event.preventDefault();
                return;
            }
            const hit = purposes.find((purpose) => hotkeyOf(purpose) === event.key.toUpperCase());
            if (hit) {
                event.preventDefault();
                onChoose(hit);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
        // onChoose/hotkeyOf hängen nur an choiceData bzw. der Sprache.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsKey, choiceData?.pendingAddress, language]);

    const address = choiceData?.pendingAddress;
    const addressText = address
        ? selectByCurrentLanguage(address.displayNameDE, address.displayNameEN) ||
          [buildStreetWithHouseNumber(address.street, address.houseNumber), address.biggerArea]
              .filter(Boolean)
              .join(", ")
        : "";

    const footer = (
        <div className="flex justify-end">
            <Button
                text
                icon={PrimeIcons.TIMES}
                label={t("purpose.choiceDialog.cancel")}
                onClick={onCancel}
                data-testid="purpose-dialog-cancel"
            />
        </div>
    );

    return (
        <Dialog
            header={t("purpose.choiceDialog.header")}
            visible={!!choiceData}
            modal={true}
            onHide={onCancel}
            style={{width: "40rem", maxWidth: "90vw"}}
            footer={footer}
            focusOnShow={false}
            pt={{root: {"data-testid": forOrder ? "purpose-choice-dialog-order" : "purpose-choice-dialog"}}}
        >
            <p className="mt-0 mb-2">
                {t(forOrder ? "purpose.choiceDialog.introOrder" : "purpose.choiceDialog.intro")}
            </p>
            {addressText && (
                <p className="mt-0 mb-4 font-bold" data-testid="purpose-dialog-address">
                    {addressText}
                </p>
            )}
            <div ref={listRef} className="flex flex-col gap-3">
                {options.map((purpose, index) => (
                    <Button
                        key={purpose}
                        outlined
                        className="w-full text-left"
                        onClick={() => onChoose(purpose)}
                        data-testid={`purpose-dialog-choose-${purpose}`}
                        // Erste Option vorfokussiert: Enter wählt sie, Escape bricht über onHide ab.
                        autoFocus={index === 0}
                    >
                        <span className={`${PURPOSE_ICONS[purpose]} text-2xl mr-4`} />
                        <span className="flex flex-col">
                            <span className="font-bold">{t(`purpose.name.${purpose}`)}</span>
                            <span className="text-sm">
                                {t(`purpose.choiceDialog.${forOrder ? "descriptionOrder" : "description"}.${purpose}`)}
                            </span>
                        </span>
                        <kbd className="ml-auto pl-4 opacity-70" data-testid={`purpose-dialog-hotkey-${purpose}`}>
                            {hotkeyOf(purpose)}
                        </kbd>
                    </Button>
                ))}
            </div>
        </Dialog>
    );
}
